import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, Phone, Zap, AlertTriangle, Shield, Clock } from 'lucide-react';

const PanelUpgrades = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Electrical Panel Upgrades
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Professional electrical panel upgrade services in London, Kingston, and surrounding areas. Ensure your home or business has the power capacity it needs with safe, code-compliant electrical panel installations.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
                <Link to="/contact">Get a Free Quote</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">
                <a href="tel:519-709-9242">
                  <Phone className="mr-2 h-5 w-5" />
                  Call 519-709-9242
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Upgrade */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Upgrade Your Electrical Panel?
              </h2>
              <p className="text-lg text-gray-600 mb-4">
                Your electrical panel is the heart of your home's electrical system. As homes age and power demands increase with modern appliances and technology, older panels may no longer meet your needs.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                An outdated or undersized panel can lead to frequent circuit breaker trips, flickering lights, and even serious safety hazards including electrical fires.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Increased Power Capacity</h3>
                    <p className="text-gray-600">Support modern appliances and electrical demands</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Enhanced Safety</h3>
                    <p className="text-gray-600">Reduce fire risk and protect your property</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Code Compliance</h3>
                    <p className="text-gray-600">Meet current electrical safety standards</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Home Value</h3>
                    <p className="text-gray-600">Increase property value and appeal to buyers</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_next-day-electric/artifacts/brvra63l_718131502001.jpg"
                alt="Electrical panel upgrade installation"
                className="rounded-lg shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Signs You Need an Upgrade */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            Signs You Need a Panel Upgrade
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card>
              <CardHeader>
                <AlertTriangle className="h-12 w-12 text-yellow-600 mb-4" />
                <CardTitle>Frequent Circuit Breaker Trips</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  If your breakers are constantly tripping, it's a sign your panel can't handle your electrical load.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Zap className="h-12 w-12 text-blue-600 mb-4" />
                <CardTitle>Flickering or Dimming Lights</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Lights that flicker or dim when appliances turn on indicate insufficient power supply.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Clock className="h-12 w-12 text-purple-600 mb-4" />
                <CardTitle>Panel Over 25 Years Old</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Older panels may contain outdated components or use obsolete fuse systems.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <AlertTriangle className="h-12 w-12 text-red-600 mb-4" />
                <CardTitle>Burning Smell or Scorch Marks</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Any burning odor or visible scorching around your panel requires immediate professional attention.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Zap className="h-12 w-12 text-green-600 mb-4" />
                <CardTitle>Adding New Appliances</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Planning to install a hot tub, EV charger, or major appliances? You may need more capacity.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Shield className="h-12 w-12 text-blue-900 mb-4" />
                <CardTitle>Fuse Box System</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  If you still have a fuse box instead of circuit breakers, an upgrade is highly recommended.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Panel Upgrade Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            Our Panel Upgrade Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="border-2 border-blue-900">
              <CardHeader>
                <CardTitle className="text-2xl">Residential Panel Upgrades</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">100 amp to 200 amp service upgrades</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">Fuse box to circuit breaker conversions</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">Sub-panel installations</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">AFCI and GFCI protection upgrades</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">ESA inspection coordination</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-900">
              <CardHeader>
                <CardTitle className="text-2xl">Commercial Panel Upgrades</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">High-capacity commercial panel installations</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">Three-phase panel upgrades</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">Distribution board modernization</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">Emergency power systems integration</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">Minimal business disruption scheduling</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
            Our Panel Upgrade Process
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            We handle every aspect of your electrical panel upgrade with professionalism and attention to detail
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="bg-blue-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="font-semibold text-lg mb-2">Assessment</h3>
              <p className="text-gray-600">We evaluate your current system and power needs</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="font-semibold text-lg mb-2">Planning</h3>
              <p className="text-gray-600">Design the upgrade and obtain necessary permits</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="font-semibold text-lg mb-2">Installation</h3>
              <p className="text-gray-600">Professional installation by licensed electricians</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                4
              </div>
              <h3 className="font-semibold text-lg mb-2">Inspection</h3>
              <p className="text-gray-600">ESA inspection and final testing for safety</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Upgrade Your Electrical Panel?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contact Studerus Electric Inc. today for a free consultation and quote on your electrical panel upgrade
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button asChild size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
              <Link to="/contact">Get Free Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">
              <a href="tel:519-709-9242">
                <Phone className="mr-2 h-5 w-5" />
                Call 519-709-9242
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PanelUpgrades;
